"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { AlertTriangleIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useRouter } from "@/lib/navigation";
import { toProjectIdFromSaViewId } from "../shared/sa-view-id";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import type { DeleteSearchAnalyticsViewInput } from "./schema";

const DeleteSaViewConfirmQuery = graphql(`
  query DeleteSaViewConfirmQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on SearchAnalyticsView {
        id
        name
        description
      }
    }
  }
`);

const ProjectDataQuery = graphql(`
  query SaViewDeleteConfirmProjectData($id: ID!) {
    node(id: $id) {
      __typename
      ... on SearchAnalyticsProject {
        name
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

/**
 * Search Analytics View の削除確認 UI を表示する。
 * ツール入力が揃ったタイミングで詳細をフェッチして一覧化する。
 */
export function DeleteSearchAnalyticsViewConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const router = useRouter();
  const isTarget =
    isToolUIPart(part) && getToolName(part) === "delete_search_analytics_view";
  const isInputAvailable = part.state === "input-available";
  const input = (part.input ?? {}) as Partial<DeleteSearchAnalyticsViewInput>;
  const viewId = input.id ?? "";

  const [{ data, fetching }] = useQuery({
    query: DeleteSaViewConfirmQuery,
    variables: { id: viewId },
    pause: !(isTarget && isInputAvailable && viewId),
  });

  const projectId = viewId ? toProjectIdFromSaViewId(viewId) : null;
  const [{ data: projectData, fetching: projectFetching }] = useQuery({
    query: ProjectDataQuery,
    variables: { id: projectId ?? "" },
    pause: !(isTarget && isInputAvailable && projectId),
  });

  if (!isTarget || !isInputAvailable) return null;

  const viewNode =
    data?.node?.__typename === "SearchAnalyticsView" ? data.node : null;
  return (
    <div className="p-4 text-sm space-y-4">
      <div>
        <div className="mb-2 font-medium">
          <Trans>Do you want to delete this Search Analytics view?</Trans>
        </div>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <Trans>Name</Trans>:{" "}
            {fetching ? (
              <Skeleton className="h-4 w-24" />
            ) : (
              <span className="font-medium">
                {viewNode?.name ?? <Trans>Unknown view</Trans>}
              </span>
            )}
          </li>
          {viewNode?.description ? (
            <li>
              <Trans>Description</Trans>: {viewNode.description}
            </li>
          ) : null}
          {projectId ? (
            <li>
              <Trans>Project</Trans>:{" "}
              {projectFetching ? (
                <Skeleton className="h-4 w-24" />
              ) : (
                <span className="font-medium">
                  {projectData?.node?.__typename ===
                  "SearchAnalyticsProject" ? (
                    projectData.node.name
                  ) : (
                    <Trans>Unknown project</Trans>
                  )}
                </span>
              )}
            </li>
          ) : null}
        </ul>
      </div>
      <Alert variant="destructive" className="text-xs">
        <AlertTriangleIcon className="h-4 w-4" />
        <AlertTitle>
          <Trans>This action is irreversible</Trans>
        </AlertTitle>
        <AlertDescription>
          <Trans>
            The saved filters and metrics for this view, and any watch rules
            that monitor it, will be permanently removed.
          </Trans>
        </AlertDescription>
      </Alert>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
          variant="destructive"
          onSuccess={() => router.refresh()}
        >
          <Trans>Delete</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
